import * as ProDuotoneSvgIcons from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormikContext } from 'formik'
import * as React from 'react'
import { Link, SetURLSearchParams, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { EnvelopeIcon } from 'src/assets/icons/customIcons/Envelope'
import { getLecturerDegreeName } from 'src/helpers/fns'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useUserStateMutateOnMount } from 'src/hooks/userState'
import Warning from 'src/imgs/classroom_icon.svg'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import ErrorElement from 'src/views/components/ErrorElement'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import { SortButtons } from 'src/views/components/SortButtons'
import UserPhoto from 'src/views/components/UserPhoto'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import { SearchCancelButtons } from 'src/views/components/forms/formik/SearchCancelButtons'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import UserEmulationButton from './AdministratorUsersEmulate/UserEmulateButton'
import useEmulateUser from './AdministratorUsersEmulate/useEmulateUser'

const DEFAULT_PAGE = '1'
const DEFAULT_PER_PAGE = '10'
const PER_PAGE_OPTIONS = [
  { value: 10, label: 10 },
  { value: 20, label: 20 },
  { value: 50, label: 50 },
  { value: 1000, label: 1000 },
]

interface LecturersFormValues {
  readonly query: string
  readonly degree: string
}

const FORM_KEYS: (keyof LecturersFormValues)[] = ['query', 'degree']

export default function AdministratorUsersLecturersPage(): JSX.Element | null {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={`${t('system:users')} - ${t('lecturer:lecturers')}`}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const locale = useLocale()
  useUserStateMutateOnMount()
  const t = useTranslatable()

  const breadcrumbsItems = React.useMemo(
    () => [
      {
        page: `${t('system:users')} - ${t('lecturer:lecturers')}`,
        path: `/${locale}/administrator/users/lecturers`,
      },
    ],
    [locale, t]
  )

  return (
    <Section
      title={t('system:users')}
      breadcrubms={breadcrumbsItems}
      icon={<FontAwesomeIcon icon={ProDuotoneSvgIcons.faUsers} />}
    >
      <FilterForm />
      <SuspenseWrapper>
        <UsersTable />
      </SuspenseWrapper>
    </Section>
  )
}

function FilterForm(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams({
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE,
    query: '',
    degree: '',
  })
  const t = useTranslatable()

  const query = searchParams.get('query') || ''
  const degree = searchParams.get('degree') || ''

  const initialValues: LecturersFormValues = { query, degree }
  const headers = useAuthenticatedHeaders()

  const handleFormSubmit = React.useCallback(
    (values: LecturersFormValues) => {
      setSearchParams((params) => {
        FORM_KEYS.forEach((key) => {
          const value = values[key] ?? ''
          if (value) params.set(key, value)
          else params.delete(key)
        })
        params.set('page', DEFAULT_PAGE)
        return params
      })
    },
    [setSearchParams]
  )

  const { data: userTypes } = useApi({
    endpoint: Api.getAdministratorDefinitionsUsers,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  const degreeOptions = React.useMemo(() => {
    if (!userTypes) return [{ value: '', label: t('common:any') }]
    return [
      { value: '', label: t('common:any') },
      ...userTypes.lecturerTypes.map((d: { value: string; label: string }) => ({
        value: String(d.value),
        label: d.label,
      })),
    ]
  }, [userTypes, t])

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      classNames={{ form: 'w-full px-2 mt-3 mb-1' }}
      enableReinitialize
    >
      <fieldset className="flex w-full flex-col justify-between">
        <FormError />
        <div className="mb-3 flex w-full flex-wrap justify-between gap-2">
          <div className="mr-0 w-full min-w-[100px] md:!mb-0 md:mr-2 md:w-[49%]">
            <TextInput
              type="text"
              label={t('common:enter_your_keyword')}
              name="query"
              placeholder={t('common:enter_your_keyword')}
            />
          </div>
          <div className="w-full min-w-[100px] md:w-[49%]">
            <SelectInput label={t('common:type')} name="degree" options={degreeOptions} />
          </div>
        </div>
        <FilterActions setSearchParams={setSearchParams} currentQuery={query} currentType={degree} />
      </fieldset>
    </Form>
  )
}

function FilterActions({
  setSearchParams,
  currentQuery,
  currentType,
}: {
  setSearchParams: SetURLSearchParams
  currentQuery: string
  currentType: string
}): JSX.Element {
  const formik = useFormikContext<LecturersFormValues>()
  const isDirty = formik.values.query !== currentQuery || formik.values.degree !== currentType
  const canCancel = !currentQuery && !currentType
  return (
    <SearchCancelButtons
      isDirty={isDirty}
      canCancel={canCancel}
      onCancel={() => {
        setSearchParams({
          query: '',
          degree: '',
          page: DEFAULT_PAGE,
          perPage: DEFAULT_PER_PAGE,
        })
        formik.resetForm()
        formik.submitForm()
      }}
    />
  )
}

function UsersTable(): JSX.Element | null {
  const emulateUser = useEmulateUser()
  const locale = useLocale()
  const headers = useAuthenticatedHeaders()
  useUserStateMutateOnMount()
  const t = useTranslatable()

  const [searchParams] = useSearchParams({
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE,
    query: '',
    degree: '',
  })

  const query = searchParams.get('query') || ''
  const degree = searchParams.get('degree') || ''
  const page = searchParams.get('page') || DEFAULT_PAGE
  const perPage = searchParams.get('perPage') || DEFAULT_PER_PAGE
  const sortBy = searchParams.get('sortBy') || 'firstName:asc'

  const { data: usersResponse, error } = useApi({
    endpoint: Api.getAdministratorLecturerUsers,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          sortBy,
          page,
          perPage,
          filters: { search: query || null, degree: degree || null },
        },
      }),
      [headers, sortBy, page, perPage, query, degree]
    ),
  })

  if (error != null) {
    return <ErrorElement errorProp={error as base.CommonError} />
  }
  if (usersResponse?.data != null && usersResponse.data.length === 0) {
    return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
  }
  if (usersResponse?.meta == null) return null

  const users = usersResponse.data ?? []
  const pagination = usersResponse.meta.pagination

  return (
    <div className="w-full py-3">
      <PaginationTop pagination={pagination} showTotalCount options={{ perPageOptions: PER_PAGE_OPTIONS }} />
      <Table.Table className="mt-7 w-full min-w-[768px] table-auto">
        <Table.Thead>
          <Table.Tr>
            <Table.Th className="w-[80px] min-w-[80px]">
              <div className="flex items-center gap-2">
                <span>ID</span>
                <SortButtons sortField="id" />
              </div>
            </Table.Th>
            <Table.Th className="flex w-[200px] min-w-[200px] items-center">
              <div className="flex items-center gap-2">
                <span>
                  {t('person:firstname')}/{t('person:lastname')}
                </span>
                <SortButtons sortField="firstName" />
              </div>
            </Table.Th>
            <Table.Th className="min-w-[120px]">
              <div className="flex items-center gap-2">
                <span>{t('person:personal_number')}</span>
                <SortButtons sortField="personalNumber" />
              </div>
            </Table.Th>
            <Table.Th className="min-w-[120px]">
              <div className="flex items-center gap-2">
                <span>{t('common:type')}</span>
                <SortButtons sortField="degree" />
              </div>
            </Table.Th>
            <Table.Th className="min-w-[100px]" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {users.map((user) => (
            <Table.Tr key={user.id}>
              <Table.Td className="min-w-[80px] rounded-l-lg">{user.id}</Table.Td>
              <Table.Td className="min-w-[200px] align-middle">
                <div className="flex items-center">
                  <UserPhoto user={user} />
                  <span className="ml-2">{user.fullName}</span>
                </div>
              </Table.Td>
              <Table.Td className="min-w-[120px] rounded-r-lg">{user.personalNumber}</Table.Td>
              <Table.Td className="min-w-[120px] rounded-r-lg">
                {getLecturerDegreeName(user.profiles?.[0]?.degree as Api.LecturerDegree)}
              </Table.Td>
              <Table.Td className="min-w-[100px]">
                <div className="flex items-center">
                  <Link to={`/${locale}/messages/compose/${user.uid}`} className="flex rounded-[18px] p-[10px] text-xs">
                    <span className="w-[18px]">
                      <EnvelopeIcon />
                    </span>
                  </Link>
                  <UserEmulationButton user={user} emulateUser={emulateUser} />
                </div>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table.Table>
      <PaginationBottom pagination={pagination} options={{ perPageOptions: PER_PAGE_OPTIONS }} />
    </div>
  )
}
